import { isSameDay } from 'date-fns';
import {
	deriveFixtureMatchStatus,
	GameMatchStatus,
	isLobbyActivityOngoing,
	isOnCourtFixture,
	isOpticOddsSuspended,
	ProbabilityMatchStatus,
	type LobbyMatchItemDto,
	type UserLobbyActivityMatchDto,
	UserLobbyActivityMatchesGroupedDto,
	type UserPredictedMatchDto,
} from '@sportspredict/shared';
import {
	getTeamMatchSport,
	getTeamMatchPhase,
	isActiveTeamMatchPhase,
} from './team-sport-match.utils';
import type { ProbabilityMatch, ProbabilitySlate } from '../types/probability-event.types';
import { TRACK_FORECASTS_LIMIT } from '../constants/track-forecasts.constants';
import { formatProbabilityEventDay, formatProbabilityEventDuration } from './date.utils';

export enum WinnerSide {
	HOME = 'HOME',
	AWAY = 'AWAY',
}

export const getMatchWinner = (match: ProbabilityMatch): WinnerSide | null => {
	const { result } = match;
	if (!result) return null;

	// An explicit winner outranks the score, and has to be read before the score guard:
	// a walkover carries no scores at all, yet OO still names who advances.
	if (result.winner === 'home') return WinnerSide.HOME;
	if (result.winner === 'away') return WinnerSide.AWAY;

	if (result.homeScore == null || result.awayScore == null) return null;
	if (result.homeScore > result.awayScore) return WinnerSide.HOME;
	if (result.awayScore > result.homeScore) return WinnerSide.AWAY;
	return null;
};

export const getMatchStartDate = (match: any) => {
	const rawDate = match?.opening_time || match?.start_date || match?.startDate || match?.start_time;
	if (!rawDate) return null;

	const date = new Date(rawDate);
	return Number.isNaN(date.getTime()) ? null : date;
};

export const getMatchClosingDate = (match: any) => {
	const rawDate = match?.closing_time;
	if (!rawDate) return null;

	const date = new Date(rawDate);
	return Number.isNaN(date.getTime()) ? null : date;
};

export const isMatchOpen = (match: any, nowMs: number) => {
	const status = getMatchStatus(match, nowMs);
	return (
		status === ProbabilityMatchStatus.IN_PROGRESS ||
		status === ProbabilityMatchStatus.CLOSES_TODAY ||
		status === ProbabilityMatchStatus.CLOSES_TOMORROW
	);
};

export const getSlateMatches = (slate?: ProbabilitySlate | null) =>
	Array.isArray(slate?.matches)
		? [...slate.matches].sort((matchA, matchB) => {
				const dateA = getMatchStartDate(matchA)?.getTime() ?? 0;
				const dateB = getMatchStartDate(matchB)?.getTime() ?? 0;
				if (dateA !== dateB) return dateA - dateB;

				const orderA = Number.isFinite(Number(matchA?.order)) ? Number(matchA.order) : 0;
				const orderB = Number.isFinite(Number(matchB?.order)) ? Number(matchB.order) : 0;
				return orderA - orderB;
			})
		: [];

export const getAllSlateMatches = (slates: ProbabilitySlate[] = []): ProbabilityMatch[] =>
	slates.flatMap((slate) => getSlateMatches(slate).map((match) => ({ ...match, slate })));

export function getCircularItem<T>(items: T[], activeIndex: number, offset: number): T | undefined {
	if (items.length <= 1) return undefined;
	const nextIndex = (activeIndex + offset + items.length) % items.length;
	return items[nextIndex];
}

export function getCircularMatch(
	matches: LobbyMatchItemDto[],
	activeIndex: number,
	offset: number
): LobbyMatchItemDto | undefined {
	return getCircularItem(matches, activeIndex, offset);
}

export function groupMatchesByDay(matches: ProbabilityMatch[]) {
	const sorted = [...matches].sort((matchA, matchB) => {
		const dateA = getMatchStartDate(matchA)?.getTime() ?? 0;
		const dateB = getMatchStartDate(matchB)?.getTime() ?? 0;
		return dateA - dateB;
	});

	const grouped = new Map<string, ProbabilityMatch[]>();
	for (const match of sorted) {
		const startDate = getMatchStartDate(match);
		const dayLabel = startDate ? formatProbabilityEventDay(startDate) : 'TBD';
		grouped.set(dayLabel, [...(grouped.get(dayLabel) || []), match]);
	}

	return Array.from(grouped.entries()).map(([dayLabel, dayMatches]) => ({
		dayLabel,
		matches: dayMatches,
	}));
}

export const AWAITING_START_TEXT = 'Awaiting start...';
export const BACK_SOON_TEXT = 'Back soon...';

export type MatchStatus = ProbabilityMatchStatus | null;

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

export interface MatchStatusStyle {
	background: string;
	backgroundSolid: string;
	textColor: string;
	borderRadius: string;
}

const MATCH_STATUS_STYLES: Record<Exclude<MatchStatus, null>, MatchStatusStyle> = {
	[ProbabilityMatchStatus.PRE_MATCH]: {
		background: 'rgba(220, 227, 249, 0.48)',
		backgroundSolid: '#DCE3F9',
		textColor: '#5583F6',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.CLOSES_TOMORROW]: {
		background: 'rgba(228, 217, 255, 0.48)',
		backgroundSolid: '#E4D9FF',
		textColor: '#8338EC',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.CLOSES_TODAY]: {
		background: 'rgba(228, 217, 255, 0.48)',
		backgroundSolid: '#E4D9FF',
		textColor: '#8338EC',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.CLOSES_SOON]: {
		background: 'rgba(255, 206, 34, 0.2)',
		backgroundSolid: '#FFCE22',
		textColor: '#AD851F',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.IN_PROGRESS]: {
		background: '#FBE7E7',
		backgroundSolid: '#FBE7E7',
		textColor: '#DA3030',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.SUSPENDED]: {
		background: '#AD851F',
		backgroundSolid: '#AD851F',
		textColor: '#FFFFFF',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.WALKOVER]: {
		background: '#E5E5E5',
		backgroundSolid: '#848484',
		textColor: '#474747',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.RETIREMENT]: {
		background: '#E5E5E5',
		backgroundSolid: '#848484',
		textColor: '#474747',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.ABANDONED]: {
		background: '#E87D7D',
		backgroundSolid: '#931F1F',
		textColor: '#611818',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.CANCELLED]: {
		background: '#E87D7D',
		backgroundSolid: '#931F1F',
		textColor: '#611818',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.SETTLING]: {
		background: '#FEF3C7',
		backgroundSolid: '#FEF3C7',
		textColor: '#D97706',
		borderRadius: '24px',
	},
	[ProbabilityMatchStatus.SETTLED]: {
		background: '#BDF4C3',
		backgroundSolid: '#BDF4C3',
		textColor: '#14AE5C',
		borderRadius: '8px',
	},
};

export const getMatchStatusStyle = (status: MatchStatus): MatchStatusStyle | null => {
	if (!status) return null;
	return MATCH_STATUS_STYLES[status];
};

export const isInProgressStatus = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.IN_PROGRESS;

export const isSuspendedStatus = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.SUSPENDED;

export const isWalkoverStatus = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.WALKOVER;

export const isRetirementStatus = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.RETIREMENT;

export const isAbandonedStatus = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.ABANDONED;

export const isCancelledStatus = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.CANCELLED;

/** Ended without a full result: no play at all, or stopped part-way. */
export const isAbnormalEndStatus = (status: MatchStatus): boolean =>
	isWalkoverStatus(status) ||
	isRetirementStatus(status) ||
	isAbandonedStatus(status) ||
	isCancelledStatus(status);

/** Any of the three red states, suspension included. */
export const isAbnormalMatchStatus = (status: MatchStatus): boolean =>
	isSuspendedStatus(status) || isAbnormalEndStatus(status);

/** Still on court: playing, or paused and expected back. */
export const isLiveOrSuspendedStatus = (status: MatchStatus): boolean =>
	isInProgressStatus(status) || isSuspendedStatus(status);

/** Nothing left to forecast — the match is over, however it ended. */
export const isFinishedMatchStatus = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.SETTLED ||
	status === ProbabilityMatchStatus.SETTLING ||
	isAbnormalEndStatus(status);

/**
 * A winner exists and may be highlighted. Not a suspension: that one is unresolved.
 * Abandoned/Cancelled are abnormal ends too, but per design neither shows a WINNER tag —
 * an abandoned match's partial result is not a declared winner, and a cancelled one has none.
 */
export const showsMatchWinner = (status: MatchStatus): boolean =>
	status === ProbabilityMatchStatus.SETTLED ||
	(isAbnormalEndStatus(status) && !isAbandonedStatus(status) && !isCancelledStatus(status));

/** Opens the Score Tracker rather than the markets page. */
export const isScoreTrackerStatus = (status: MatchStatus): boolean =>
	isInProgressStatus(status) || isAbnormalMatchStatus(status);

/**
 * The trading window is over and the markets page has to hand off to the Score
 * Tracker / Match Summary. Status-driven on purpose: a postponed fixture is past its
 * opening_time for hours while OpticOdds still reports it as not started, and reading
 * the clock there took the markets away from users who could still forecast (JWB-4679).
 */
export const isTradingClosedStatus = (status: MatchStatus): boolean =>
	isScoreTrackerStatus(status) || isFinishedMatchStatus(status);

const FIXTURE_STATUS_BADGE: Partial<Record<GameMatchStatus, ProbabilityMatchStatus>> = {
	[GameMatchStatus.Walkover]: ProbabilityMatchStatus.WALKOVER,
	[GameMatchStatus.Retired]: ProbabilityMatchStatus.RETIREMENT,
	[GameMatchStatus.Suspended]: ProbabilityMatchStatus.SUSPENDED,
	[GameMatchStatus.Abandoned]: ProbabilityMatchStatus.ABANDONED,
	[GameMatchStatus.Cancelled]: ProbabilityMatchStatus.CANCELLED,
};

export const getMatchStatus = (
	match: ProbabilityMatch,
	nowMs: number,
	isTennis = false
): MatchStatus => {
	const startsAt = getMatchStartDate(match);
	const closesAt = getMatchClosingDate(match);

	// The OO-driven half of the decision is shared with the backend deriver, so the
	// precedence rule (retirement outranks "completed") is not restated here. Final is
	// deliberately absent from the map: SETTLED vs SETTLING needs all_markets_settled,
	// which only the date branch below knows about.
	const fromFixture = deriveFixtureMatchStatus(
		match.result?.fixtureStatus,
		match.result?.retirementInfo,
		undefined,
		match.result?.hasScore ?? undefined
	);
	const fixtureBadge = fromFixture ? FIXTURE_STATUS_BADGE[fromFixture] : undefined;
	if (fixtureBadge) return fixtureBadge;

	if (!startsAt) return null;

	const startsAtMs = startsAt.getTime();
	const closesAtMs = closesAt?.getTime();

	// SETTLING: Match is over but not all markets have settled yet
	// SETTLED: Match is over and all markets are settled
	if (closesAtMs && closesAtMs <= nowMs) {
		if (match.all_markets_settled === false) {
			return ProbabilityMatchStatus.SETTLING;
		}
		return ProbabilityMatchStatus.SETTLED;
	}

	// IN PROGRESS: Kickoff has occurred, trading window is closed.
	// Tennis: don't flip until OpticOdds confirms the match is actually live —
	// stay CLOSES_SOON (badge unchanged) while awaiting the official trigger.
	if (startsAtMs <= nowMs) {
		if (isTennis && !isOnCourtFixture(match.result)) {
			return ProbabilityMatchStatus.CLOSES_SOON;
		}
		return ProbabilityMatchStatus.IN_PROGRESS;
	}

	// CLOSES SOON: Kickoff is in less than 2 hours (highest priority)
	if (startsAtMs - nowMs < TWO_HOURS_MS) {
		return ProbabilityMatchStatus.CLOSES_SOON;
	}

	const now = new Date(nowMs);
	const startsAtDate = new Date(startsAtMs);

	// CLOSES TODAY: Kickoff is today in user's local time
	if (isSameDay(startsAtDate, now)) {
		return ProbabilityMatchStatus.CLOSES_TODAY;
	}

	// CLOSES TOMORROW: Kickoff is tomorrow in user's local time
	const tomorrow = new Date(nowMs);
	tomorrow.setDate(tomorrow.getDate() + 1);
	if (isSameDay(startsAtDate, tomorrow)) {
		return ProbabilityMatchStatus.CLOSES_TOMORROW;
	}

	return ProbabilityMatchStatus.PRE_MATCH;
};

export const applyMatchUpdate = <T extends { id: string }>(
	list: T[],
	match: { id: string } & Partial<T>
): T[] => list.map((m) => (m.id === match.id ? { ...m, ...match } : m));

export const applyMatchUpdateInSlates = <T extends { id: string }, S extends { matches?: T[] }>(
	slates: S[],
	match: Partial<T> & { id: string }
): S[] =>
	slates.map((slate) => ({
		...slate,
		matches: slate.matches?.map((m) => (m.id === match.id ? { ...m, ...match } : m)),
	}));

type PredictedMatchBuckets<T> = { live: T[]; preMatch: T[] };

/** Merge socket match update; re-bucket when `result.isLive` is set. */
export const applyMatchUpdateToPredictedMatches = <
	T extends { id: string; result?: { isLive?: boolean; fixtureStatus?: string | null } | null },
>(
	predicted: PredictedMatchBuckets<T>,
	match: { id: string } & Partial<T>,
	categoryId?: string | null
): PredictedMatchBuckets<T> => {
	const fromLive = predicted.live.find((m) => m.id === match.id);
	const fromPreMatch = predicted.preMatch.find((m) => m.id === match.id);
	const existing = fromLive ?? fromPreMatch;

	if (!existing) {
		return {
			live: applyMatchUpdate(predicted.live, match),
			preMatch: applyMatchUpdate(predicted.preMatch, match),
		};
	}

	const merged = { ...existing, ...match } as T;
	const liveWithout = predicted.live.filter((m) => m.id !== match.id);
	const preMatchWithout = predicted.preMatch.filter((m) => m.id !== match.id);
	if (getTeamMatchSport(categoryId)) {
		if (match.result) merged.result = { ...existing.result, ...match.result };
		const phase = getTeamMatchPhase(merged);
		const place = (list: T[]) =>
			list.some((m) => m.id === merged.id)
				? list.map((m) => (m.id === merged.id ? merged : m))
				: [...list, merged];
		if (phase === 'settled' || phase === 'cancelled')
			return { live: liveWithout, preMatch: preMatchWithout };
		if (isActiveTeamMatchPhase(phase))
			return { live: place(predicted.live), preMatch: preMatchWithout };
		if (phase === 'upcoming') return { live: liveWithout, preMatch: place(predicted.preMatch) };
		return fromLive
			? { live: place(predicted.live), preMatch: preMatchWithout }
			: { live: liveWithout, preMatch: place(predicted.preMatch) };
	}
	// A suspension keeps the match on court, so it belongs in the live bucket even
	// though OO drops the live flag while play is stopped.
	const isSuspended = isOpticOddsSuspended(merged.result?.fixtureStatus);
	const isLiveFlag = merged.result?.isLive;

	if (isLiveFlag === true || isSuspended) {
		return { live: [...liveWithout, merged], preMatch: preMatchWithout };
	}
	if (isLiveFlag === false) {
		return { live: liveWithout, preMatch: [...preMatchWithout, merged] };
	}

	if (fromLive) {
		return { live: [...liveWithout, merged], preMatch: preMatchWithout };
	}
	return { live: liveWithout, preMatch: [...preMatchWithout, merged] };
};

export const toUserPredictedMatchDto = (match: UserLobbyActivityMatchDto): UserPredictedMatchDto =>
	({
		id: match.matchId,
		name: match.name,
		opening_time: match.opening_time,
		closing_time: match.closing_time,
		home_competitors: match.home_competitors,
		away_competitors: match.away_competitors,
		relative_brier_score: match.relative_brier_score,
		prediction_count: match.markets?.length ?? 0,
		markets_count: match.totalMarkets,
		// Carried through so the Track cards and the split below read the fixture status
		// instead of falling back to the clock (JWB-4679).
		result: match.result ?? undefined,
	}) as UserPredictedMatchDto;

export const splitOngoingActivityForTrack = (
	ongoing: UserLobbyActivityMatchDto[] | undefined,
	nowMs: number,
	isTennis = false
): { live: UserPredictedMatchDto[]; preMatch: UserPredictedMatchDto[] } => {
	const live: UserPredictedMatchDto[] = [];
	const preMatch: UserPredictedMatchDto[] = [];

	for (const activityMatch of ongoing ?? []) {
		if (!activityMatch?.matchId) continue;
		const match = toUserPredictedMatchDto(activityMatch);
		const openingMs = new Date(match.opening_time).getTime();
		const closingMs = new Date(match.closing_time).getTime();
		if (closingMs <= nowMs) continue;
		// The same rule the API groups by (probability.service: getUserPredictedMatchesGrouped),
		// so the bot Track tabs cannot disagree with the user's own: tennis is live when
		// OpticOdds says it is on court, every other sport once its opening_time has passed.
		const isLive = isTennis ? isOnCourtFixture(match.result) : openingMs <= nowMs;
		if (isLive) live.push(match);
		else preMatch.push(match);
	}

	// Same cap as getUserPredictedMatches (home Track Your Forecasts): soonest N per tab.
	return {
		live: live.slice(0, TRACK_FORECASTS_LIMIT),
		preMatch: preMatch.slice(0, TRACK_FORECASTS_LIMIT),
	};
};

export const isUserLobbyActivityMatch = (
	entry: UserLobbyActivityMatchDto | null | undefined
): entry is UserLobbyActivityMatchDto => !!entry?.matchId;

export const updateActivityWithMatches = (
	activity: UserLobbyActivityMatchesGroupedDto,
	updates: (UserLobbyActivityMatchDto | null | undefined)[]
): UserLobbyActivityMatchesGroupedDto => {
	const validUpdates = updates.filter(isUserLobbyActivityMatch);
	if (!validUpdates.length) return activity;

	const map = new Map(validUpdates.map((m) => [m.matchId, m]));
	const mergeEntry = (m: UserLobbyActivityMatchDto) => {
		const updated = map.get(m.matchId);
		return updated ?? m;
	};

	return {
		ongoing: (activity.ongoing ?? []).filter(isUserLobbyActivityMatch).map(mergeEntry),
		settled: (activity.settled ?? []).filter(isUserLobbyActivityMatch).map(mergeEntry),
	};
};

export const applyMatchUpdatedToActivity = (
	activity: UserLobbyActivityMatchesGroupedDto,
	match: any
): UserLobbyActivityMatchesGroupedDto => {
	const allMatches = [...(activity.ongoing ?? []), ...(activity.settled ?? [])].filter(
		isUserLobbyActivityMatch
	);

	const updatedMatches = allMatches.map((m) =>
		m.matchId !== match.id
			? m
			: {
					...m,
					...(match?.opening_time !== undefined && { opening_time: match.opening_time }),
					...(match?.closing_time !== undefined && { closing_time: match.closing_time }),
					...((match?.result?.homeScore !== undefined ||
						match?.result?.awayScore !== undefined ||
						match?.result?.fixtureStatus !== undefined ||
						match?.result?.retirementInfo !== undefined) && {
						result: {
							...m.result,
							...(match?.result?.homeScore !== undefined && { homeScore: match.result.homeScore }),
							...(match?.result?.awayScore !== undefined && { awayScore: match.result.awayScore }),
							// Without these the socket update would drop the status and the badge
							// would fall back to the clock.
							...(match?.result?.fixtureStatus !== undefined && {
								fixtureStatus: match.result.fixtureStatus,
							}),
							...(match?.result?.retirementInfo !== undefined && {
								retirementInfo: match.result.retirementInfo,
							}),
						},
					}),
				}
	);

	// One clock read for both passes, and the same predicate the API groups by, so the
	// two cannot disagree about which tab a match belongs to.
	const nowMs = Date.now();

	return {
		ongoing: updatedMatches.filter((m) => isLobbyActivityOngoing(m.closing_time, m.result, nowMs)),
		settled: updatedMatches.filter((m) => !isLobbyActivityOngoing(m.closing_time, m.result, nowMs)),
	};
};

export const formatRbp = (rbp: number | null | undefined): string => {
	if (rbp == null) return '-';
	const numericRbp = typeof rbp === 'number' ? rbp : Number(rbp);
	if (Number.isNaN(numericRbp)) return '-';
	const rounded = Number(numericRbp.toFixed(2));
	if (rounded === 0) return '0';
	return rounded > 0 ? `+ ${rounded}` : `- ${Math.abs(rounded)}`;
};

export const getMatchTimerText = (
	match: ProbabilityMatch,
	nowMs: number | null | undefined,
	isTennis = false
): string => {
	if (nowMs == null) return '';

	const status = getMatchStatus(match, nowMs, isTennis);
	if (
		status === ProbabilityMatchStatus.SETTLED ||
		status === ProbabilityMatchStatus.SETTLING ||
		isInProgressStatus(status) ||
		isAbnormalEndStatus(status)
	) {
		return '';
	}

	// Reuses the awaiting-start slot: same position, same styling, different wording.
	if (isSuspendedStatus(status)) return BACK_SOON_TEXT;

	if (status === ProbabilityMatchStatus.CLOSES_TODAY) {
		const closesAt = getMatchStartDate(match);
		return formatProbabilityEventDuration(closesAt, nowMs);
	}

	const startsAt = getMatchStartDate(match);
	if (!startsAt) return '';

	// On Zero Behavior: tennis waits for the real OpticOdds trigger instead of
	// flipping straight to a "0m" countdown once the estimated kickoff passes.
	if (isTennis && startsAt.getTime() <= nowMs) {
		return AWAITING_START_TEXT;
	}

	return formatProbabilityEventDuration(startsAt, nowMs);
};

export interface MatchTimer {
	/** Countdown / estimated-start text. Empty once the match is live or settled. */
	text: string;
	/** Tennis On-Zero state: the estimated kickoff passed but OpticOdds has not triggered yet. */
	isAwaitingStart: boolean;
	/** Leading label for the countdown row, or null when the timer stands alone. */
	label: string | null;
}

interface GetMatchTimerOptions {
	/** Clickable cards drop the non-tennis label to keep the header on one line (JWB-4506). */
	isClickable?: boolean;
}

export const getMatchTimer = (
	match: ProbabilityMatch,
	nowMs: number | null | undefined,
	isTennis = false,
	{ isClickable = true }: GetMatchTimerOptions = {}
): MatchTimer => {
	const text = getMatchTimerText(match, nowMs, isTennis);
	const isAwaitingStart = text === AWAITING_START_TEXT || text === BACK_SOON_TEXT;
	const showLabel = !!text && !isAwaitingStart && (isTennis || !isClickable);

	return {
		text,
		isAwaitingStart,
		label: showLabel ? (isTennis ? 'Est. start' : 'Kicks off in') : null,
	};
};
